/**
 * Привязка платья к модели CRM по серии (`/api/dresses/{id}/crm`).
 *
 * Не привязано — поле «Серия или артикул» (номер экземпляра или артикул) → сервис ищет модель: одна — привязывает, несколько —
 * список моделей кнопками (название, экземпляров), нет — «в CRM не найден».
 * Привязано — плашка «Серия X · модель Y · прокат от N ₽ · отвязать»; цену и
 * наличие сервис читает из CRM при каждом показе, у себя не хранит.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { useState } from "react";

import { btn, btnGhost, ErrorLine, input } from "@/components/trends/ui";
import { bindDressCrm, loadDressCrm, photoKeys, unbindDressCrm, type CrmBind } from "@/lib/photos";
import { formatRub } from "@/lib/post";
import { retryQuery } from "@/lib/trends";

export function CrmSeries({ dressId, onBound }: { dressId: number; onBound?: () => unknown }) {
  const client = useQueryClient();
  const q = useQuery({ queryKey: photoKeys.crm(dressId), queryFn: () => loadDressCrm(dressId), retry: retryQuery });
  const [series, setSeries] = useState("");
  const [answer, setAnswer] = useState<CrmBind | null>(null);

  const changed = () => {
    void client.invalidateQueries({ queryKey: photoKeys.crm(dressId) });
    void client.invalidateQueries({ queryKey: ["photos", "dress", dressId, "crm-match"] }); // метка «привязано» в поиске по фото
    return onBound?.();
  };
  const bind = useMutation({
    mutationFn: (modelId?: number) => bindDressCrm(dressId, series.trim(), modelId),
    onSuccess: (got) => {
      setAnswer(got);
      if (got.status === "bound") return changed();
    },
  });
  const unbind = useMutation({
    mutationFn: () => unbindDressCrm(dressId),
    onSuccess: () => {
      setAnswer(null);
      return changed();
    },
  });

  if (q.isPending) return <span className="text-[12.5px] text-fg-faint">Загружаю…</span>;
  if (q.isError) return <ErrorLine error={q.error} />;
  const crm = q.data;

  if (crm.crm_model_id !== null) {
    const offer = crm.offer;
    const shown = crm.series ?? offer?.series ?? "—";
    return (
      <div className="grid gap-1" data-annot="серия crm">
        <span className="flex flex-wrap items-center gap-x-1.5 text-[12.5px] text-fg">
          <span>Серия {shown}</span>
          <span className="text-fg-faint">·</span>
          <span>модель {offer?.name ?? `#${crm.crm_model_id}`}</span>
          {offer ? (
            <>
              <span className="text-fg-faint">·</span>
              {offer.price_from !== null ? (
                <span>прокат от {formatRub(offer.price_from)} ₽</span>
              ) : (
                <span className="text-warn">в салонах нет в наличии</span>
              )}
            </>
          ) : null}
          <span className="text-fg-faint">·</span>
          <button type="button" className={btnGhost} disabled={unbind.isPending} onClick={() => unbind.mutate()}>
            отвязать
          </button>
        </span>
        {crm.crm_error ? <span className="text-[12px] text-warn">{crm.crm_error}</span> : null}
        <ErrorLine error={unbind.error} />
      </div>
    );
  }

  const busy = bind.isPending;
  return (
    <div className="grid gap-1.5" data-annot="серия crm">
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy && series.trim()) bind.mutate(undefined);
        }}
      >
        <input
          aria-label="Серия или артикул"
          className={clsx(input, "w-36")}
          placeholder="Серия или артикул CRM"
          maxLength={20}
          value={series}
          onChange={(e) => {
            setSeries(e.target.value);
            setAnswer(null);
          }}
        />
        <button type="submit" className={btn} disabled={busy || !series.trim()}>
          {busy ? "Ищу…" : "Привязать"}
        </button>
        {answer?.status === "not_found" ? <span className="text-[12.5px] text-warn">в CRM нет такой серии или артикула (платья)</span> : null}
      </form>
      {answer?.status === "choose" ? (
        <div className="grid gap-1" data-annot="модели серии">
          <span className="text-[11.5px] text-fg-faint">У серии несколько моделей — выберите:</span>
          <div className="flex flex-wrap gap-1.5">
            {answer.models.map((m) => (
              <button key={m.model_id} type="button" className={btn} disabled={busy} onClick={() => bind.mutate(m.model_id)}>
                {m.name ?? `#${m.model_id}`}
                <span className="tnum text-fg-faint">{m.serials} экз.</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
      <ErrorLine error={bind.error} />
    </div>
  );
}
